import React, { useRef, useState } from 'react';
import { Send, CheckCircle2, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { useLang } from '../context/LanguageContext';
import { api } from '../lib/api';
import TurnstileWidget, { turnstileConfigured } from './TurnstileWidget';

export default function ContactFormSection({ inline = false }) {
  const { t, lang } = useLang();
  const [form, setForm] = useState({ first: '', last: '', email: '', help: '', message: '' });
  const [token, setToken] = useState('');
  const captchaRef = useRef(null);
  const [sent, setSent] = useState(false);
  const [sending, setSending] = useState(false);

  const handle = (e) => setForm({ ...form, [e.target.name]: e.target.value });
  const submit = async (e) => {
    e.preventDefault();
    if (!form.first || !form.email || !form.message) {
      toast.error(t('cf.errFill'));
      return;
    }
    if (turnstileConfigured && !token) {
      toast.error(t('cf.errCaptcha'));
      return;
    }
    setSending(true);
    try {
      await api.submitContactForm({
        ...form, locale: lang || '', turnstile_token: token,
      });
      const all = JSON.parse(localStorage.getItem('wyse_leads') || '[]');
      all.push({ ...form, at: new Date().toISOString() });
      localStorage.setItem('wyse_leads', JSON.stringify(all));
      setSent(true);
      toast.success(t('cf.success'));
      setTimeout(() => {
        setSent(false);
        setForm({ first: '', last: '', email: '', help: '', message: '' });
        setToken('');
        captchaRef.current?.reset();
      }, 3500);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not send. Try again.');
      setToken('');
      captchaRef.current?.reset();
    } finally {
      setSending(false);
    }
  };

  const wrapper = inline
    ? 'bg-[#faf6ec] rounded-xl p-8 md:p-10'
    : 'bg-white rounded-xl p-8 md:p-12 shadow-[0_25px_60px_-25px_rgba(20,17,15,0.2)]';

  return (
    <div className={wrapper}>
      <p className="section-eyebrow mb-3">{t('cf.eyebrow')}</p>
      <h3 className="text-[28px] md:text-[34px] font-extrabold text-[#14110f] leading-tight">
        {t('cf.titlePre')} <span className="text-[#f5b300]">{t('cf.titleAccent')}</span>
      </h3>
      <form onSubmit={submit} className="mt-7 space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <input name="first" value={form.first} onChange={handle} placeholder={t('cf.first')} className="px-4 py-3.5 rounded-md bg-white border border-neutral-200 focus:border-[#f5b300] focus:ring-2 focus:ring-[#f5b300]/20 outline-none text-[14px]" />
          <input name="last" value={form.last} onChange={handle} placeholder={t('cf.last')} className="px-4 py-3.5 rounded-md bg-white border border-neutral-200 focus:border-[#f5b300] focus:ring-2 focus:ring-[#f5b300]/20 outline-none text-[14px]" />
        </div>
        <div className="grid grid-cols-2 gap-4">
          <input name="email" type="email" value={form.email} onChange={handle} placeholder={t('cf.email')} className="px-4 py-3.5 rounded-md bg-white border border-neutral-200 focus:border-[#f5b300] focus:ring-2 focus:ring-[#f5b300]/20 outline-none text-[14px]" />
          <input name="help" value={form.help} onChange={handle} placeholder={t('cf.help')} className="px-4 py-3.5 rounded-md bg-white border border-neutral-200 focus:border-[#f5b300] focus:ring-2 focus:ring-[#f5b300]/20 outline-none text-[14px]" />
        </div>
        <textarea name="message" value={form.message} onChange={handle} rows={5} placeholder={t('cf.message')} className="w-full px-4 py-3.5 rounded-md bg-white border border-neutral-200 focus:border-[#f5b300] focus:ring-2 focus:ring-[#f5b300]/20 outline-none text-[14px] resize-none" />

        <TurnstileWidget ref={captchaRef}
                         onVerify={setToken}
                         onExpire={() => setToken('')}
                         testId="contact-turnstile" />

        <button
          type="submit"
          data-testid="contact-form-submit-btn"
          disabled={sent || sending}
          className="inline-flex items-center gap-2 bg-[#f5b300] hover:bg-[#e0a200] disabled:bg-emerald-500 disabled:hover:bg-emerald-500 text-[#14110f] disabled:text-white font-semibold px-7 py-3.5 rounded-md shadow-[0_10px_28px_-10px_rgba(245,179,0,0.7)] hover:-translate-y-0.5 transition-all"
        >
          {sent ? (<><CheckCircle2 className="w-4 h-4" /> {t('cta.submitted')}</>)
            : sending ? (<><Loader2 className="w-4 h-4 animate-spin" /> {t('cta.submitNow')}</>)
            : (<>{t('cta.submitNow')} <Send className="w-4 h-4" /></>)}
        </button>
      </form>
    </div>
  );
}
